import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { api, type User } from "../lib/api";

interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { user } = await api.get<{ user: User }>("/auth/me");
      setUser(user);
    } catch {
      setUser(null);          // not signed in; the sign-in screen handles it
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // Any request that comes back 401 means the session ended elsewhere.
  useEffect(() => {
    const onSignedOut = () => setUser(null);
    window.addEventListener("brinley:signed-out", onSignedOut);
    return () => window.removeEventListener("brinley:signed-out", onSignedOut);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { user } = await api.post<{ user: User }>("/auth/login", { email, password });
    setUser(user);
  }, []);

  const signOut = useCallback(async () => {
    try { await api.post("/auth/logout"); } finally { setUser(null); }
  }, []);

  return <Ctx.Provider value={{ user, loading, signIn, signOut, refresh }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** The brand mark, from the client's own site. Two files: dark ink for light
 *  backgrounds, white for dark ones. */
export function Logo({ className, variant }: { className?: string; variant?: "dark" | "white" }) {
  if (variant) return <img className={className} src={`/api/brand/logo.svg?variant=${variant}`} alt="Shipwreck Rum" />;
  return (
    <picture className={className}>
      <source srcSet="/api/brand/logo.svg?variant=white" media="(prefers-color-scheme: dark)" />
      <img src="/api/brand/logo.svg?variant=dark" alt="Shipwreck Rum" />
    </picture>
  );
}
